import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Container,
  Paper,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAxios } from "../../../../hooks";
import { useNavigate, useParams } from "react-router-dom";
import { CancelButton } from "../../../css components/Button";
import { colors } from "../../../css components/Style";
import styled from "styled-components";
import ChannelAnalyticsCard from "./ChannelAnalyticsCard";

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default function CampaignAnalyticsConsumer() {
  const { campaignId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const axios = useAxios();

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios({
        url: `/get/stats/${campaignId}`,
        method: "GET",
        disableError: true,
      });
      console.log(response);
      if (response?.status === true) {
        setData(response);
      } else {
        setError(
          response?.message.errorMessage || "Failed to fetch analytics data"
        );
      }
    } catch (err) {
      setError("No stats data available for this campaign.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (campaignId) {
      fetchAnalytics();
    }
  }, [campaignId]);

  // Calculate overall totals
  const calculateTotals = () => {
    if (!data?.data?.channels?.length) return null;

    return data.data.channels.reduce(
      (totals, channel) => {
        const stats = channel.stats_data || {};
        return {
          initiated: totals.initiated + (stats.initiated || 0),
          sent: totals.sent + (stats.sent || 0),
          delivered: totals.delivered + (stats.delivered || 0),
          read: totals.read + (stats.read || 0),
          failed: totals.failed + (stats.failed || 0),
        };
      },
      { initiated: 0, sent: 0, delivered: 0, read: 0, failed: 0 }
    );
  };

  const totals = calculateTotals();

  const getDeliveryRate = (stats) => {
    const total =
      (stats.initiated || 0) +
      (stats.sent || 0) +
      (stats.delivered || 0) +
      (stats.read || 0) +
      (stats.failed || 0);
    if (total === 0) return 0;
    return Math.round(
      (((stats.delivered || 0) + (stats.read || 0)) / total) * 100
    );
  };

  if (loading) {
    return (
      <Wrapper
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: "#02A0FC" }} />
      </Wrapper>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Campaign Analytics
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {data?.data.campaign_name}
            </Typography>
          </Box>
          <CancelButton onClick={() => navigate(-1)} variant="outlined">
            Back
          </CancelButton>
        </Box>
      </Box>

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && data && (
        <>
          {/* Channel-wise Performance */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Channel Performance
            </Typography>

            <Grid container spacing={3}>
              {data.data.channels.length === 0 ? (
                <Grid item xs={12}>
                  <Paper sx={{ p: 6, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No channel data available yet.
                    </Typography>
                  </Paper>
                </Grid>
              ) : (
                data.data.channels.map((channel, index) => (
                  <Grid item xs={12} key={index}>
                    <ChannelAnalyticsCard channel={channel} />
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        </>
      )}
    </Container>
  );
}
